import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  GetRecurringTransactionsDocument,
  GetRecurringTransactionDocument,
  type GetRecurringTransactionsQuery,
  type GetRecurringTransactionQuery,
} from "@/src/gql/graphql";
import type {
  RecurringTransaction,
  RecurringTransactionFilters,
} from "@/lib/types/recurring-transactions";
import {
  mapRecurringTransactionEdges,
  mapRecurringTransactionNode,
} from "@/lib/types/quickbooks/recurring-transactions";

export function useRecurringTransactions(filters?: RecurringTransactionFilters) {
  const { data, loading, error, refetch } = useQuery<GetRecurringTransactionsQuery>(
    GetRecurringTransactionsDocument,
    {
      variables: {
        filter: {
          ...(filters?.customerId && { customer_id: { eq: filters.customerId } }),
          ...(filters?.companyId && { company_id: { eq: filters.companyId } }),
          ...(filters?.syncStatus && { sync_status: { eq: filters.syncStatus } }),
          ...(filters?.lastSyncedMonth && { last_synced_month: { eq: filters.lastSyncedMonth } }),
        },
      },
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    }
  );

  const transactions: RecurringTransaction[] = useMemo(
    () => mapRecurringTransactionEdges(data?.recurring_transactionsCollection?.edges),
    [data]
  );

  return {
    data: transactions,
    loading,
    error,
    refetch,
    totalCount: data?.recurring_transactionsCollection?.totalCount ?? transactions.length,
    pageInfo: data?.recurring_transactionsCollection?.pageInfo,
  };
}

export function useRecurringTransaction(id: string) {
  const { data, loading, error, refetch } = useQuery<GetRecurringTransactionQuery>(
    GetRecurringTransactionDocument,
    {
      variables: { id },
      errorPolicy: "all",
      skip: !id,
    }
  );

  const transaction: RecurringTransaction | null = useMemo(() => {
    const node = data?.recurring_transactionsCollection?.edges?.[0]?.node;
    if (!node) {
      return null;
    }
    return mapRecurringTransactionNode(node);
  }, [data]);

  return { data: transaction, loading, error, refetch };
}
